import React, { useEffect, useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faMagnifyingGlass,
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../services/Interceptor";
import { getCandidateAthletes } from "../../../services/AdminService";
import CandidateAthletesMobileList from "./CandidateAthletesMobileList";

const PAGE_SIZE = 25;

const COLUMNS = [
  { label: "First Name", field: "firstName" },
  { label: "Last Name", field: "lastName" },
  { label: "Email", field: "email" },
  { label: "Event Name", field: "eventName" },
  { label: "HighSchool", field: "highSchool" },
  { label: "Expected Year Of Graduation", field: "expectedYearOfGraduation" },
  { label: "Athlete PhoneNumber", field: "athletePhoneNumber" },
  { label: "Parent PhoneNumber", field: "parentPhoneNumber" },
  { label: "City", field: "city" },
  { label: "State", field: "state" },
  { label: "Date", field: "createdAtUTC" },
];

const normalize = (str) =>
  (str ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${month}/${day}/${d.getFullYear()}`;
};

function SortIcon({ sortField, activeSortField, sortDir }) {
  if (sortField !== activeSortField)
    return (
      <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-400 text-xs" />
    );
  return (
    <FontAwesomeIcon
      icon={sortDir === "asc" ? faSortUp : faSortDown}
      className="ml-1 text-red-600 text-xs"
    />
  );
}

function AdminCandidateAthletes() {
  const apiInstance = useAxiosInstance();
  const [athletes, setAthletes] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pastWeek: 0,
    past24Hours: 0,
    pastMonth: 0,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getCandidateAthletes(
          apiInstance,
          page,
          PAGE_SIZE,
        );
        if (Array.isArray(response)) {
          setAthletes(response);
        } else {
          setAthletes(Array.isArray(response?.results) ? response.results : []);
          if (response?.stats) setStats(response.stats);
          if (response?.totalPages) setTotalPages(response.totalPages);
          if (response?.totalResults) setTotalResults(response.totalResults);
        }
      } catch {
        setError("Failed to load candidate athletes.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const filteredAthletes = useMemo(() => {
    const q = normalize(searchQuery).trim();
    if (!q) return athletes;
    const tokens = q.split(/\s+/).filter(Boolean);
    return athletes.filter((a) => {
      const haystack = `${normalize(a.firstName)} ${normalize(a.lastName)} ${normalize(a.email)} ${normalize(a.highSchool)}`;
      return tokens.every((t) => haystack.includes(t));
    });
  }, [athletes, searchQuery]);

  const sortedAthletes = useMemo(() => {
    if (!sortField) return filteredAthletes;
    return [...filteredAthletes].sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      const cmp = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
      });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredAthletes, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const exportCSV = () => {
    const headers = COLUMNS.map((c) => c.label);
    const rows = sortedAthletes.map((a) =>
      COLUMNS.map((c) => {
        const val =
          c.field === "createdAtUTC"
            ? formatDate(a[c.field])
            : (a[c.field] ?? "");
        return `"${String(val).replace(/"/g, '""')}"`;
      }),
    );
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "candidate-athletes.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const startRecord = totalResults === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(page * PAGE_SIZE, totalResults);

  return (
    <div className="p-4 bg-gray-100 h-full overflow-y-auto overflow-x-hidden">
      {/* Desktop / Large screen view */}
      <div className="hidden lg:block min-w-0">
        <div className="bg-white shadow-lg rounded-lg p-4">
          <h1 className="text-xl mb-3 uppercase text-gray-900">
            Potential Athlete Signups
          </h1>

          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex gap-10">
              <StatItem label="TOTAL" value={stats.total} />
              <StatItem label="THIS WEEK" value={stats.pastWeek} />
              <StatItem label="NEW TODAY" value={stats.past24Hours} />
              <StatItem label="PAST MONTH" value={stats.pastMonth} />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 shadow-sm transition-colors"
              >
                <FontAwesomeIcon icon={faDownload} />
                Export CSV
              </button>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  placeholder="Name/Email/School"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-400 w-52"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-500">Loading...</div>
          ) : error ? (
            <div className="text-center py-10 text-red-500">{error}</div>
          ) : (
            <>
              {/* Scrollable table — overflow constrained here, not on the page */}
              <div className="overflow-auto w-full max-h-[70vh]">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-red-100">
                      {COLUMNS.map((col) => (
                        <th
                          key={col.field}
                          onClick={() => handleSort(col.field)}
                          className="px-3 py-2 text-left font-semibold text-gray-900 cursor-pointer border border-red-200 select-none hover:bg-red-200 transition-colors"
                        >
                          {col.label}
                          <SortIcon
                            sortField={col.field}
                            activeSortField={sortField}
                            sortDir={sortDir}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAthletes.length === 0 ? (
                      <tr>
                        <td
                          colSpan={COLUMNS.length}
                          className="text-center py-10 text-gray-500"
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      sortedAthletes.map((a, i) => (
                        <tr
                          key={a.id ?? i}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-3 py-1 text-gray-800">
                            {a.firstName}
                          </td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.lastName}
                          </td>
                          <td className="px-3 py-1 text-gray-800">{a.email}</td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.eventName}
                          </td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.highSchool}
                          </td>
                          <td className="px-3 py-1 text-center text-gray-800">
                            {a.expectedYearOfGraduation}
                          </td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.athletePhoneNumber}
                          </td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.parentPhoneNumber}
                          </td>
                          <td className="px-3 py-1 text-gray-800">{a.city}</td>
                          <td className="px-3 py-1 text-gray-800">{a.state}</td>
                          <td className="px-3 py-1 text-gray-800">
                            {formatDate(a.createdAtUTC)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination — always shown when there are results */}
              {totalResults > 0 && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Showing {startRecord}–{endRecord} of {totalResults} results
                  </p>
                  <div className="flex items-center gap-1">
                    <PageButton
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page === 1}
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="text-xs"
                      />
                    </PageButton>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (p) =>
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - page) <= 1,
                      )
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) =>
                        p === "..." ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className="px-2 text-gray-400 text-sm"
                          >
                            …
                          </span>
                        ) : (
                          <PageButton
                            key={p}
                            onClick={() => setPage(p)}
                            active={p === page}
                          >
                            {p}
                          </PageButton>
                        ),
                      )}

                    <PageButton
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page === totalPages}
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="text-xs"
                      />
                    </PageButton>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile / Tablet view */}
      <div className="block lg:hidden">
        <CandidateAthletesMobileList
          athletes={sortedAthletes}
          stats={stats}
          searchQuery={searchQuery}
          onSearchChange={(v) => {
            setSearchQuery(v);
            setPage(1);
          }}
          onExport={exportCSV}
          loading={loading}
          error={error}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          startRecord={startRecord}
          endRecord={endRecord}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}

function StatItem({ label, value }) {
  return (
    <div className="pt-4">
      <p className="text-xs text-gray-600 uppercase">{label}</p>
      <p className="text-2xl font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function PageButton({ onClick, disabled, active, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-8 h-8 flex items-center justify-center rounded text-sm transition-colors
        ${active ? "bg-red-500 text-white font-semibold" : ""}
        ${!active && !disabled ? "hover:bg-gray-100 text-gray-700" : ""}
        ${disabled ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      {children}
    </button>
  );
}

export default AdminCandidateAthletes;
